import React, { FC, memo, useMemo } from "react";
import { Annotation } from "../../../interfaces/annotation";
import { useAppSelector } from "../../../../app";
import AnnotationItemConnection from "./AnnotationItemConnection";
import AnnotationItemToken from "./AnnotationItemToken";
import tokensGeometrySelector from "../../../../annotation/selectors/tokensGeometrySelector";
import useScrollToAnnotation from "../../../hooks/useScrollToAnnotation";
import { shallowEqual } from "react-redux";

type Props = {
  page: number;
  annotation: Annotation;
};

const AnnotationItem: FC<Props> = ({ page, annotation }) => {
  const scrollClassName = useScrollToAnnotation(annotation);

  const isTempAnnotation = annotation.tempAnnotation || false;
  const color = annotation.entity.color;

  // Get geometry only (doesn't change on highlights)
  const geometryParams = useMemo(
    () => ({
      pageNumber: page,
      tokenIndices: annotation.pageTokenIndices,
      isTempAnnotation,
      color,
      entityId: annotation.entity.id,
      index: annotation.index!,
    }),
    [
      page,
      annotation.pageTokenIndices,
      isTempAnnotation,
      color,
      annotation.entity.id,
      annotation.index,
    ],
  );

  const geometryItems = useAppSelector(
    (state) => tokensGeometrySelector(state, geometryParams),
    shallowEqual,
  );

  // Extract geometry data only - style generation moved to AnnotationItemToken
  const items = useMemo(() => {
    if (!geometryItems) {
      return null;
    }

    return geometryItems.map((item) => ({
      id: item.id,
      coords: item.coords,
      rotation: item.rotation,
    }));
  }, [geometryItems]);

  if (!items?.length) {
    return null;
  }

  return (
    <div
      className="annotation-layer__item-container"
      data-annotation-id={annotation.id}
      data-annotation-index={annotation.index}
      data-entity-id={annotation.entity.id}
      data-is-temp={isTempAnnotation}
    >
      {items.map((item, index) => (
        <AnnotationItemToken
          key={item.id}
          id={item.id}
          annotationId={annotation.id}
          entityId={annotation.entity.id}
          index={annotation.index || 1}
          coords={item.coords}
          rotation={item.rotation}
          color={color}
          scrollClassName={scrollClassName}
          annotationIndex={annotation.index || 1}
          tokenIndex={index}
          isTempAnnotation={isTempAnnotation}
        />
      ))}
      <AnnotationItemConnection
        vertices={[...items]
          .map((i) => i.coords)
          .sort((a, b) => a.left - b.left)}
        color={annotation.entity.color}
      />
    </div>
  );
};

// Custom comparison - only re-render if annotation data actually changed
const areEqual = (prevProps: Props, nextProps: Props) => {
  return (
    prevProps.page === nextProps.page &&
    prevProps.annotation.id === nextProps.annotation.id &&
    prevProps.annotation.index === nextProps.annotation.index &&
    prevProps.annotation.entity.id === nextProps.annotation.entity.id &&
    prevProps.annotation.pageTokenIndices ===
      nextProps.annotation.pageTokenIndices &&
    prevProps.annotation.entity.color === nextProps.annotation.entity.color &&
    prevProps.annotation.tempAnnotation === nextProps.annotation.tempAnnotation
  );
};

export default memo(AnnotationItem, areEqual);
